import { useCallback, useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { completeAssuranceByBrand, packagesFor } from "../../config/pricing.js";

// ServiceLad sells these as the Full HPI Check and the HPI check with service history.
const packages = packagesFor("servicelad");
const RISK_NAME = packages[0].name;
import { createOrder, createPaymentIntent, confirmPayment, getStripePublishableKey } from "../../services/paymentApi";
import { getVehicle } from "../../services/vehicleApi";
import { getWalletRecoveryAction } from "../../utils/paymentRecovery";
import { cleanRegistration, formatUkRegistration, isValidUkRegistration } from "../../utils/registration";
import { Icon } from "./Icon";

function isAppleMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export default function SlPackagesModal({ open, onClose, brand, initialReg = "" }) {
  const [step, setStep] = useState("packages"); // packages | checkout
  const [selectedKey, setSelectedKey] = useState("complete");
  const [regInput, setRegInput] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [regError, setRegError] = useState("");
  const [payError, setPayError] = useState("");
  const [busy, setBusy] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [stripeInstance, setStripeInstance] = useState(null);
  const [stripeElements, setStripeElements] = useState(null);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [applePayProbeDone, setApplePayProbeDone] = useState(false);
  const [expressAvailable, setExpressAvailable] = useState(false);
  const [walletMountVersion, setWalletMountVersion] = useState(0);
  const [status, setStatus] = useState(null); // checking | paid | no_history | partial | null
  const [statusMessage, setStatusMessage] = useState("");
  const [partialMeta, setPartialMeta] = useState(null);

  const emailRef = useRef(null);
  const cardMountRef = useRef(null);
  const cardElementRef = useRef(null);
  const expressCheckoutMount = useRef(null);
  const appleStripeMount = useRef(null);
  const paymentBusy = useRef(false);
  const walletRecoveryBusy = useRef(false);
  const walletWasOpened = useRef(false);
  const paymentRequestRef = useRef(null);
  const walletStripeRef = useRef(null);
  const liveRef = useRef({});
  const appleMobile = isAppleMobileDevice();

  const plan = packages.find((item) => item.key === selectedKey) || packages[0];
  const cleanReg = cleanRegistration(regInput);
  const displayReg = cleanReg ? formatUkRegistration(cleanReg) : "";

  // Read inside finishPaidOrder, which is memoised once.
  const selectedKeyRef = useRef(selectedKey);
  selectedKeyRef.current = selectedKey;

  const finishPaidOrder = useCallback(async (paidOrderId, paymentIntentId) => {
    setStatus("checking");
    setStatusMessage(selectedKeyRef.current === "complete"
      ? "Your payment is only authorised. We will take it only if manufacturer service records are found."
      : "This takes a few seconds. Please keep this page open.");
    try {
      for (let attempt = 0; attempt < 12; attempt += 1) {
        const confirmResult = await confirmPayment(paidOrderId, paymentIntentId);
        if (["no_history", "history_unavailable", "canceled"].includes(confirmResult.status)) {
          setStatus("no_history");
          setStatusMessage(confirmResult.message || "Your payment was not taken.");
          return;
        }
        if (confirmResult.status === "partially_paid") {
          setStatus("partial");
          setPartialMeta({
            serviceHistoryStatus: confirmResult.service_history_status,
            capturedPence: confirmResult.captured_pence,
            releasedPence: confirmResult.released_pence,
            message: confirmResult.message,
            reportToken: confirmResult.report_token || null,
          });
          return;
        }
        if (confirmResult.report_token) {
          window.location.href = `/report?token=${confirmResult.report_token}`;
          return;
        }
        if (confirmResult.status === "paid") {
          setStatus("paid");
          setStatusMessage("Payment confirmed. A receipt and report link will be emailed to you.");
          return;
        }
        if (confirmResult.status !== "checking") break;
        await new Promise((resolve) => window.setTimeout(resolve, 1250));
      }
      throw new Error("The service-record check is taking longer than expected. Your payment has not been taken yet.");
    } catch (err) {
      setStatus(null);
      setPayError(err.message || "Could not finish your order.");
    }
  }, []);

  liveRef.current = {
    selectedKey,
    regInput,
    email,
    brandKey: brand.key,
    plan,
    finishPaidOrder,
  };

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const initialRegRef = useRef(initialReg);
  initialRegRef.current = initialReg;
  const wasOpenRef = useRef(false);

  const resetPaymentSurface = useCallback(() => {
    cardElementRef.current?.destroy?.();
    cardElementRef.current = null;
    setPaymentReady(false);
    setOrderId(null);
    setClientSecret("");
    setStripeInstance(null);
    setStripeElements(null);
    setExpressAvailable(false);
    setWalletMountVersion(0);
    walletWasOpened.current = false;
    setStatus(null);
    setStatusMessage("");
    setPartialMeta(null);
    setPayError("");
  }, []);

  const resetAll = useCallback(() => {
    resetPaymentSurface();
    setStep("packages");
    setSelectedKey("complete");
    setRegInput("");
    setEmail("");
    setEmailError("");
    setRegError("");
    setBusy(false);
    setApplePayAvailable(false);
    setApplePayProbeDone(false);
    paymentRequestRef.current = null;
    walletStripeRef.current = null;
  }, [resetPaymentSurface]);

  useEffect(() => {
    if (!open) {
      if (wasOpenRef.current) resetAll();
      wasOpenRef.current = false;
      return undefined;
    }

    if (!wasOpenRef.current) {
      const seed = initialRegRef.current;
      setRegInput(seed ? formatUkRegistration(cleanRegistration(seed)) : "");
    }
    wasOpenRef.current = true;

    const onKey = (event) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, resetAll]);

  // If Complete is not eligible for this plate, switch to Risk before Apple Pay can charge the wrong amount.
  useEffect(() => {
    if (!open || step !== "checkout" || selectedKey !== "complete" || paymentReady) return undefined;
    const cleaned = cleanRegistration(regInput);
    if (!isValidUkRegistration(cleaned)) return undefined;
    let active = true;
    getVehicle(cleaned)
      .then((vehicle) => {
        if (!active) return;
        if (vehicle?.serviceHistoryEligible === false || vehicle?.serviceHistoryKnownEmpty) {
          setSelectedKey("risk");
          setPayError(`Service history is not available for this vehicle. Switched to the ${RISK_NAME}.`);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [open, step, selectedKey, regInput, paymentReady]);

  // Set up Apple Pay once per checkout form visit. Amount updates separately.
  useEffect(() => {
    if (!open || step !== "checkout" || paymentReady || status) {
      setApplePayAvailable(false);
      setApplePayProbeDone(false);
      paymentRequestRef.current = null;
      return undefined;
    }

    let cancelled = false;

    const onPaymentMethod = async (event) => {
      const live = liveRef.current;
      const cleaned = cleanRegistration(live.regInput);
      if (!isValidUkRegistration(cleaned)) {
        event.complete("fail");
        setRegError("Please enter a valid registration.");
        return;
      }

      const payerEmail = (event.payerEmail || live.email || "").trim();
      if (!payerEmail) {
        event.complete("fail");
        setEmailError("Apple Pay did not return an email. Enter your email and try again.");
        return;
      }

      setBusy(true);
      setPayError("");
      try {
        let packageKey = live.selectedKey;
        try {
          const vehicle = await getVehicle(cleaned);
          if (
            packageKey === "complete"
            && (vehicle?.serviceHistoryEligible === false || vehicle?.serviceHistoryKnownEmpty)
          ) {
            packageKey = "risk";
            setSelectedKey("risk");
          }
        } catch {
          // Server still enforces eligibility on createOrder.
        }

        const chosen = packages.find((item) => item.key === packageKey) || packages[0];
        if (chosen.pricePence !== live.plan.pricePence) {
          event.complete("fail");
          setPayError("This package changed for your vehicle. Tap Apple Pay again.");
          paymentRequestRef.current?.update({
            total: { label: chosen.name, amount: chosen.pricePence },
          });
          return;
        }

        const order = await createOrder({
          email: payerEmail,
          registration: cleaned,
          brand: live.brandKey,
          totalPence: chosen.pricePence,
          items: [{ item_type: "package", label: chosen.name, price_pence: chosen.pricePence }],
        });

        const stripe = walletStripeRef.current;
        const { client_secret, publishable_key } = await createPaymentIntent(order.order_id);
        const stripeReady = stripe || await loadStripe(publishable_key || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
        if (!stripeReady) throw new Error("Payment could not start.");

        const { error, paymentIntent } = await stripeReady.confirmCardPayment(
          client_secret,
          { payment_method: event.paymentMethod.id },
          { handleActions: false },
        );
        if (error) throw error;

        event.complete("success");
        setEmail(payerEmail);
        if (["succeeded", "requires_capture"].includes(paymentIntent?.status)) {
          await live.finishPaidOrder(order.order_id, paymentIntent.id);
        } else if (paymentIntent?.status === "processing") {
          setPayError("Your payment is still processing. Please wait.");
        }
      } catch (err) {
        event.complete("fail");
        setPayError(err.message || "Apple Pay failed.");
      } finally {
        setBusy(false);
      }
    };

    const onCancel = () => {
      setBusy(false);
    };

    (async () => {
      try {
        const publishableKey = await getStripePublishableKey();
        const stripe = await loadStripe(publishableKey || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
        if (cancelled || !stripe) return;
        walletStripeRef.current = stripe;

        const currentPlan = liveRef.current.plan;
        const paymentRequest = stripe.paymentRequest({
          country: "GB",
          currency: "gbp",
          total: {
            label: currentPlan.name,
            amount: currentPlan.pricePence,
          },
          requestPayerEmail: true,
          disableWallets: ["googlePay", "link", "browserCard"],
        });

        paymentRequest.on("paymentmethod", onPaymentMethod);
        paymentRequest.on("cancel", onCancel);

        const result = await paymentRequest.canMakePayment();
        if (cancelled) return;
        if (result?.applePay) {
          paymentRequestRef.current = paymentRequest;
          setApplePayAvailable(true);
        } else {
          paymentRequestRef.current = null;
          setApplePayAvailable(false);
        }
        setApplePayProbeDone(true);
      } catch {
        if (!cancelled) {
          paymentRequestRef.current = null;
          setApplePayAvailable(false);
          setApplePayProbeDone(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      paymentRequestRef.current = null;
      setApplePayAvailable(false);
      setApplePayProbeDone(false);
    };
  }, [open, step, paymentReady, status]);

  // Official Apple Pay button from Stripe (logo + label). A clear gate on top
  // still validates the registration before paymentRequest.show().
  useEffect(() => {
    if (!open || step !== "checkout" || paymentReady || status || !applePayAvailable) return undefined;
    if (!walletStripeRef.current || !paymentRequestRef.current || !appleStripeMount.current) return undefined;

    let button;
    try {
      const elements = walletStripeRef.current.elements();
      button = elements.create("paymentRequestButton", {
        paymentRequest: paymentRequestRef.current,
        style: {
          paymentRequestButton: {
            type: "buy",
            theme: "dark",
            height: "48px",
          },
        },
      });
      button.mount(appleStripeMount.current);
    } catch {
      return undefined;
    }

    return () => {
      try {
        button.destroy();
      } catch {
        // already gone
      }
    };
  }, [open, step, paymentReady, status, applePayAvailable]);

  useEffect(() => {
    if (!paymentRequestRef.current || !applePayAvailable) return;
    paymentRequestRef.current.update({
      total: {
        label: plan.name,
        amount: plan.pricePence,
      },
    });
  }, [plan.name, plan.pricePence, applePayAvailable]);

  useEffect(() => {
    if (!open || step !== "checkout" || !paymentReady || !stripeInstance || !cardMountRef.current) return undefined;

    let cardElement;
    try {
      const cardElements = stripeInstance.elements({
        locale: "en-GB",
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#3b82f6",
            colorText: "#101418",
            colorTextSecondary: "#666d73",
            borderRadius: "10px",
            fontFamily: '"Figtree", "DM Sans", Arial, sans-serif',
            fontSizeBase: "16px",
            spacingUnit: "4px",
          },
        },
      });
      cardElement = cardElements.create("card", {
        hidePostalCode: true,
        style: {
          base: {
            color: "#101418",
            fontFamily: "DM Sans, Arial, sans-serif",
            fontSize: "16px",
            "::placeholder": { color: "#9aa0a6" },
          },
        },
      });
      cardElement.mount(cardMountRef.current);
      cardElementRef.current = cardElement;
    } catch {
      setPayError("Payment form could not load. Please close and try again.");
      return undefined;
    }

    return () => {
      cardElementRef.current = null;
      cardElement.destroy();
    };
  }, [open, step, paymentReady, stripeInstance]);

  const handleWalletConfirm = useCallback(async () => {
    if (!stripeInstance || !stripeElements || !clientSecret || !orderId || paymentBusy.current) return;
    paymentBusy.current = true;
    setPayError("");
    setBusy(true);
    try {
      const { error: submitError } = await stripeElements.submit();
      if (submitError) throw submitError;

      const returnUrl = new URL("/", window.location.origin);
      const { error: confirmError, paymentIntent } = await stripeInstance.confirmPayment({
        elements: stripeElements,
        clientSecret,
        confirmParams: { return_url: returnUrl.toString() },
        redirect: "if_required",
      });
      if (confirmError) throw confirmError;
      if (["succeeded", "requires_capture"].includes(paymentIntent?.status)) {
        await finishPaidOrder(orderId, paymentIntent.id);
      } else if (paymentIntent?.status === "processing") {
        setPayError("Your payment is still processing. Please wait.");
      }
    } catch (err) {
      setPayError(err.message || "Payment failed");
    } finally {
      paymentBusy.current = false;
      setBusy(false);
    }
  }, [clientSecret, finishPaidOrder, orderId, stripeElements, stripeInstance]);

  const recoverWalletState = useCallback(async () => {
    if (!walletWasOpened.current || !stripeInstance || !clientSecret || !orderId || paymentBusy.current || walletRecoveryBusy.current) return;
    walletRecoveryBusy.current = true;
    try {
      const { paymentIntent, error } = await stripeInstance.retrievePaymentIntent(clientSecret);
      if (error) throw error;
      const action = getWalletRecoveryAction(paymentIntent?.status);
      if (action === "complete") {
        await finishPaidOrder(orderId, paymentIntent.id);
        return;
      }
      if (action === "wait") {
        setBusy(true);
        setPayError("Your payment is still processing. Please wait.");
        return;
      }
      if (action === "expired") {
        setBusy(false);
        setPayError("This payment session has expired. Close and try again.");
        return;
      }
      if (action === "reset") {
        setBusy(false);
        setPayError("");
        setExpressAvailable(false);
        setWalletMountVersion((version) => version + 1);
      }
    } catch {
      setBusy(false);
      setPayError("The wallet window closed. You can try again or pay by card.");
      setExpressAvailable(false);
      setWalletMountVersion((version) => version + 1);
    } finally {
      walletWasOpened.current = false;
      walletRecoveryBusy.current = false;
    }
  }, [clientSecret, finishPaidOrder, orderId, stripeInstance]);

  useEffect(() => {
    if (!open || step !== "checkout" || !paymentReady || status) return undefined;
    if (!stripeInstance || !stripeElements || !clientSecret || !orderId) return undefined;
    if (!expressCheckoutMount.current) return undefined;

    setExpressAvailable(false);
    let expressElement;
    try {
      expressElement = stripeElements.create("expressCheckout", {
        business: { name: brand.name },
        billingAddressRequired: false,
        phoneNumberRequired: false,
        shippingAddressRequired: false,
        buttonHeight: 48,
        buttonTheme: { applePay: "black", googlePay: "black" },
        buttonType: { applePay: "plain", googlePay: "plain" },
        layout: { maxColumns: 1, maxRows: 1, overflow: "auto" },
        lineItems: [{ name: plan.name, amount: plan.pricePence }],
        paymentMethodOrder: appleMobile ? ["apple_pay"] : ["google_pay"],
        paymentMethods: {
          applePay: appleMobile ? "always" : "never",
          googlePay: appleMobile ? "never" : "always",
          paypal: "never",
          link: "never",
          amazonPay: "never",
          klarna: "never",
        },
      });
    } catch {
      setExpressAvailable(false);
      return undefined;
    }

    const updateExpressAvailability = (event) => {
      const methods = event?.availablePaymentMethods || {};
      const hasWalletButton = Boolean(methods.applePay || methods.googlePay);
      if (!hasWalletButton) {
        setExpressAvailable(false);
        return;
      }
      setExpressAvailable(true);
      window.requestAnimationFrame(() => {
        const frame = expressCheckoutMount.current?.querySelector("iframe");
        const height = frame?.getBoundingClientRect?.().height || 0;
        if (height < 24) setExpressAvailable(false);
      });
    };

    expressElement.on("ready", updateExpressAvailability);
    expressElement.on("availablepaymentmethodschange", updateExpressAvailability);
    expressElement.on("click", (event) => {
      walletWasOpened.current = true;
      event.resolve();
    });
    expressElement.on("confirm", handleWalletConfirm);
    expressElement.on("cancel", recoverWalletState);
    expressElement.on("loaderror", () => setExpressAvailable(false));
    expressElement.mount(expressCheckoutMount.current);

    return () => expressElement.destroy();
  }, [
    open,
    step,
    paymentReady,
    status,
    stripeInstance,
    stripeElements,
    clientSecret,
    orderId,
    brand.name,
    plan.name,
    plan.pricePence,
    appleMobile,
    handleWalletConfirm,
    recoverWalletState,
    walletMountVersion,
  ]);

  useEffect(() => {
    if (!open || step !== "checkout" || !paymentReady || status) return undefined;
    if (!stripeInstance || !clientSecret || !orderId) return undefined;

    let returnTimer;
    const checkAfterReturn = () => {
      if (document.visibilityState !== "visible") return;
      if (!walletWasOpened.current) return;
      window.clearTimeout(returnTimer);
      returnTimer = window.setTimeout(recoverWalletState, 750);
    };

    window.addEventListener("focus", checkAfterReturn);
    window.addEventListener("pageshow", checkAfterReturn);
    document.addEventListener("visibilitychange", checkAfterReturn);
    return () => {
      window.clearTimeout(returnTimer);
      window.removeEventListener("focus", checkAfterReturn);
      window.removeEventListener("pageshow", checkAfterReturn);
      document.removeEventListener("visibilitychange", checkAfterReturn);
    };
  }, [open, step, paymentReady, status, stripeInstance, clientSecret, orderId, recoverWalletState]);

  const startCheckout = (key) => {
    resetPaymentSurface();
    setSelectedKey(key);
    setStep("checkout");
  };

  const startApplePay = async () => {
    const cleaned = cleanRegistration(regInput);
    if (!cleaned || !isValidUkRegistration(cleaned)) {
      setRegError("Please enter a registration.");
      return;
    }
    setRegError("");
    setEmailError("");
    setPayError("");

    const paymentRequest = paymentRequestRef.current;
    if (!paymentRequest) {
      setPayError("Apple Pay is not ready yet. Wait a second and try again, or continue by card.");
      return;
    }

    paymentRequest.update({
      total: {
        label: plan.name,
        amount: plan.pricePence,
      },
    });

    try {
      paymentRequest.show();
    } catch (err) {
      setPayError(err.message || "Apple Pay could not open. Enter your email and continue by card.");
    }
  };

  const preparePayment = async () => {
    if (busy) return;

    const cleaned = cleanRegistration(regInput);
    if (!isValidUkRegistration(cleaned)) {
      setRegError("Please enter a valid registration.");
      return;
    }
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setEmailError("Enter your email address.");
      emailRef.current?.focus();
      return;
    }
    if (emailRef.current && !emailRef.current.validity.valid) {
      setEmailError("Enter a valid email address.");
      emailRef.current.focus();
      return;
    }

    setRegError("");
    setEmailError("");
    setPayError("");
    setBusy(true);

    try {
      let vehicle = null;
      try {
        vehicle = await getVehicle(cleaned);
      } catch {
        vehicle = null;
      }

      let packageKey = selectedKey;
      if (
        packageKey === "complete"
        && vehicle
        && (vehicle.serviceHistoryEligible === false || vehicle.serviceHistoryKnownEmpty)
      ) {
        packageKey = "risk";
        setSelectedKey("risk");
        setPayError(`Service history is not available for this vehicle. Switched to the ${RISK_NAME}.`);
      }

      const chosen = packages.find((item) => item.key === packageKey) || packages[0];
      const order = await createOrder({
        email: cleanEmail,
        registration: cleaned,
        brand: brand.key,
        totalPence: chosen.pricePence,
        items: [{ item_type: "package", label: chosen.name, price_pence: chosen.pricePence }],
      });
      setOrderId(order.order_id);

      const { client_secret, publishable_key } = await createPaymentIntent(order.order_id);
      setClientSecret(client_secret);

      const stripe = await loadStripe(publishable_key || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
      if (!stripe) throw new Error("Payment could not start.");
      setStripeInstance(stripe);

      const els = stripe.elements({
        clientSecret: client_secret,
        locale: "en-GB",
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#3b82f6",
            colorText: "#101418",
            colorTextSecondary: "#666d73",
            borderRadius: "10px",
            fontFamily: '"DM Sans", Arial, sans-serif',
            fontSizeBase: "16px",
            spacingUnit: "4px",
          },
        },
      });
      setStripeElements(els);
      setPaymentReady(true);
    } catch (err) {
      setPayError(err.message || "Could not start payment.");
      resetPaymentSurface();
    } finally {
      setBusy(false);
    }
  };

  const payByCard = async () => {
    if (!stripeInstance || !cardElementRef.current || !clientSecret || !orderId || paymentBusy.current) return;
    paymentBusy.current = true;
    setPayError("");
    setBusy(true);
    try {
      const { error, paymentIntent } = await stripeInstance.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElementRef.current,
          billing_details: { email: email.trim() },
        },
      });
      if (error) throw error;
      if (["succeeded", "requires_capture"].includes(paymentIntent?.status)) {
        await finishPaidOrder(orderId, paymentIntent.id);
      } else if (paymentIntent?.status === "processing") {
        setPayError("Your payment is still processing. Please wait.");
      }
    } catch (err) {
      setPayError(err.message || "Payment failed");
    } finally {
      paymentBusy.current = false;
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="sl-packages-backdrop" role="presentation" onClick={onClose}>
      <div
        className={`sl-packages-modal${step === "checkout" ? " is-checkout" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sl-packages-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="sl-packages-close" aria-label="Close" onClick={onClose}>
          ×
        </button>

        {status === "checking" ? (
          <div className="sl-packages-status">
            <span className="sl-packages-spinner" aria-hidden="true" />
            <h2 id="sl-packages-title">{selectedKey === "complete" ? "Checking service records" : "Confirming your payment"}</h2>
            <p>{statusMessage}</p>
          </div>
        ) : status === "paid" ? (
          <div className="sl-packages-status">
            <div className="sl-packages-status-icon" aria-hidden="true">✓</div>
            <h2 id="sl-packages-title">Payment confirmed</h2>
            <p>{statusMessage}</p>
            {displayReg ? <p className="sl-packages-status-reg">{displayReg}</p> : null}
            <button type="button" className="sl-packages-cta" onClick={onClose}>Close</button>
          </div>
        ) : status === "no_history" ? (
          <div className="sl-packages-status">
            <h2 id="sl-packages-title">No service records found</h2>
            <p>{statusMessage} You can still buy the {RISK_NAME}.</p>
            <button
              type="button"
              className="sl-packages-cta"
              onClick={() => {
                resetPaymentSurface();
                setSelectedKey("risk");
                setStep("checkout");
              }}
            >
              Choose the {RISK_NAME}
            </button>
          </div>
        ) : status === "partial" ? (
          <div className="sl-packages-status">
            <h2 id="sl-packages-title">
              {partialMeta?.serviceHistoryStatus === "no_history"
                ? "No service records found"
                : "Service records could not be confirmed"}
            </h2>
            <p>{partialMeta?.message}</p>
            <p>
              Charged £{((partialMeta?.capturedPence || 0) / 100).toFixed(2)}.
              Released £{((partialMeta?.releasedPence || 0) / 100).toFixed(2)}.
            </p>
            {partialMeta?.reportToken ? (
              <a className="sl-packages-cta" href={`/report?token=${partialMeta.reportToken}`}>
                View your report
              </a>
            ) : (
              <p>Your report link will be emailed to you.</p>
            )}
          </div>
        ) : step === "packages" ? (
          <>
            <p className="sl-packages-eyebrow">What you get</p>
            <h2 id="sl-packages-title" className="sl-packages-title">
              Two one-off reports. Pick the depth you need.
            </h2>
            <p className="sl-packages-lede">
              Free tax and MOT first. Paid reports unlock the risk and ownership story when you are ready.
            </p>
            <div className="sl-packages-grid">
              {packages.map((item) => (
                <article
                  key={item.key}
                  className={`sl-packages-card${item.featured ? " is-featured" : ""}`}
                >
                  <div className="sl-packages-card-top">
                    <span className="sl-packages-stamp">{item.stamp}</span>
                    <p className="sl-packages-price">
                      £{item.plate}
                      <span>one-off</span>
                    </p>
                  </div>
                  <h3 className="sl-packages-name">{item.name}</h3>
                  <p className="sl-packages-copy">{item.copy}</p>
                  <ul className="sl-packages-list">
                    {item.items.map((line) => (
                      <li key={line}>
                        <Icon name="check" size={14} className="sl-packages-check" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  {item.key === "complete" ? (
                    <p className="sl-packages-note">{completeAssuranceByBrand.servicelad}</p>
                  ) : null}
                  <button
                    type="button"
                    className={`sl-packages-buy${item.featured ? " is-featured" : ""}`}
                    onClick={() => startCheckout(item.key)}
                  >
                    <span className="sl-packages-buy-label">Buy</span>
                    <span className="sl-packages-buy-price">£{item.plate}</span>
                    <span className="sl-packages-buy-arrow" aria-hidden="true">→</span>
                  </button>
                </article>
              ))}
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              className="sl-packages-back"
              onClick={() => {
                resetPaymentSurface();
                setStep("packages");
              }}
            >
              ← Packages
            </button>
            <p className="sl-packages-eyebrow">Checkout</p>
            <h2 id="sl-packages-title" className="sl-packages-title">
              {plan.name}
            </h2>
            <p className="sl-packages-lede">
              £{plan.plate} one-off
              {plan.key === "complete" ? `. ${completeAssuranceByBrand.servicelad}` : "."}
            </p>

            <div className="sl-packages-field">
              <span>Registration</span>
              <div className={`sl-plate-field sl-packages-plate${regInput.replace(/\s/g, "").length > 7 ? " is-vin" : ""}`}>
                {regInput.replace(/\s/g, "").length <= 7 ? (
                  <span className="sl-plate-band" aria-hidden="true">
                    <img src="/gb-flag.svg" alt="" width="22" height="14" />
                    UK
                  </span>
                ) : null}
                <div className="sl-plate-type">
                  {!regInput ? <span className="sl-plate-ph" aria-hidden="true">ENTER REG</span> : null}
                  <input
                    id="sl-packages-vrm"
                    className="sl-plate-input"
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={10}
                    placeholder=""
                    value={regInput}
                    disabled={paymentReady || busy}
                    aria-label="UK registration"
                    onChange={(event) => {
                      const next = event.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, "");
                      setRegInput(next);
                      if (regError) setRegError("");
                    }}
                  />
                </div>
              </div>
              {regError ? <small role="alert">{regError}</small> : null}
            </div>

            <label className="sl-packages-field">
              <span>Email for your report</span>
              <input
                ref={emailRef}
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                disabled={paymentReady || busy}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (emailError) setEmailError("");
                }}
              />
              {emailError ? <small role="alert">{emailError}</small> : null}
              {!paymentReady ? (
                <em className="sl-packages-field-hint">Not needed for Apple Pay. Required if you pay by card.</em>
              ) : null}
            </label>

            {!paymentReady ? (
              <>
                {payError ? <p className="sl-packages-pay-error" role="alert">{payError}</p> : null}
                {!applePayProbeDone || applePayAvailable ? (
                  <div className={`sl-packages-apple-wrap${applePayAvailable ? " is-ready" : ""}`}>
                    <div className="sl-packages-apple-shell" aria-hidden={!applePayAvailable}>
                      <div ref={appleStripeMount} className="sl-packages-apple-stripe" />
                      {applePayAvailable ? (
                        <button
                          type="button"
                          className="sl-packages-apple-gate"
                          aria-label="Buy with Apple Pay"
                          disabled={busy}
                          onClick={startApplePay}
                        />
                      ) : null}
                    </div>
                    <p className="sl-packages-or">or pay by card</p>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="sl-packages-cta"
                  disabled={busy}
                  onClick={preparePayment}
                >
                  {busy ? "Preparing payment..." : "Continue to payment"}
                </button>
              </>
            ) : (
              <div className="sl-packages-pay">
                <div className={`sl-packages-express${expressAvailable ? " is-ready" : ""}`}>
                  <div ref={expressCheckoutMount} />
                </div>
                {expressAvailable ? <p className="sl-packages-or">or pay by card</p> : null}
                <p className="sl-packages-pay-label">Card details</p>
                <div className="sl-packages-card-mount" ref={cardMountRef} />
                {payError ? <p className="sl-packages-pay-error" role="alert">{payError}</p> : null}
                <button
                  type="button"
                  className="sl-packages-cta"
                  disabled={busy}
                  onClick={payByCard}
                >
                  {busy
                    ? "Processing..."
                    : `${selectedKey === "complete" ? "Authorise" : "Pay"} £${plan.plate} securely`}
                </button>
                {selectedKey === "complete" ? (
                  <p className="sl-packages-hold">{completeAssuranceByBrand.servicelad}</p>
                ) : null}
                <p className="sl-packages-secure">Encrypted and securely processed by Stripe.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
